// A little flower that grows with training progress: seed -> sprout ->
// seedling -> bud -> blooming -> full bloom across the training window. Drawn
// as SVG so it stays crisp at any size and recolors to brand. Petals radiate
// from and attach to the center; a solid stem (caule) joins the bloom to the
// leaves.

type FlowerStage = 1 | 2 | 3 | 4 | 5 | 6;

// Solid green — a gradient on the vertical stem would render as nothing
// (a zero-width bounding box makes an objectBoundingBox gradient degenerate).
const STEM = '#4E7047';

// Day thresholds mirror the six illustrated stages. Day-based (not a fraction)
// so the ranges match what was designed against the 21-day window.
function flowerStageForDay(trainingDay: number): FlowerStage {
    if (trainingDay <= 0) {
        return 1;
    }
    if (trainingDay <= 4) {
        return 2;
    }
    if (trainingDay <= 9) {
        return 3;
    }
    if (trainingDay <= 14) {
        return 4;
    }
    if (trainingDay <= 18) {
        return 5;
    }
    return 6;
}

function StageArt({ stage }: { readonly stage: FlowerStage }) {
    const shadow = (
        <ellipse
            cx="60"
            cy="147"
            rx="26"
            ry="4.5"
            fill="var(--training)"
            opacity="0.08"
        />
    );
    switch (stage) {
        case 1:
            return (
                <g>
                    {shadow}
                    <g transform="rotate(-16 60 126)">
                        <ellipse
                            cx="60"
                            cy="126"
                            rx="10"
                            ry="15"
                            fill="#A06E44"
                        />
                        <path
                            d="M52 122 C58 118 62 118 68 122"
                            stroke="#6E4A2E"
                            strokeWidth="1.6"
                            fill="none"
                            opacity="0.5"
                        />
                        <path
                            d="M60 112 C61 106 64 103 67 101"
                            stroke="#83A65C"
                            strokeWidth="2.4"
                            fill="none"
                            strokeLinecap="round"
                        />
                    </g>
                </g>
            );
        case 2:
            return (
                <g>
                    {shadow}
                    <path
                        d="M60 146 V112"
                        stroke={STEM}
                        strokeWidth="4"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <g transform="translate(60 116)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf)" />
                    </g>
                    <g transform="translate(60 116) scale(-1 1)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf2)" />
                    </g>
                </g>
            );
        case 3:
            return (
                <g>
                    {shadow}
                    <path
                        d="M60 146 V78"
                        stroke={STEM}
                        strokeWidth="4.5"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <g transform="translate(60 120)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf)" />
                    </g>
                    <g transform="translate(60 112) scale(-1 1)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf2)" />
                    </g>
                    <g transform="translate(60 96) scale(0.8)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf)" />
                    </g>
                </g>
            );
        case 4:
            return (
                <g>
                    {shadow}
                    <path
                        d="M60 146 V50"
                        stroke={STEM}
                        strokeWidth="6"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <g transform="translate(60 120)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf)" />
                    </g>
                    <g transform="translate(60 108) scale(-1 1)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf2)" />
                    </g>
                    <path
                        d="M60 52 C51 52 48 42 52 34 C56 30 64 30 68 34 C72 42 69 52 60 52 Z"
                        fill="url(#tf-bud)"
                    />
                    <path
                        d="M60 62 C54 60 51 54 52 48 C57 50 60 54 60 62 Z"
                        fill="#4E7047"
                    />
                    <path
                        d="M60 62 C66 60 69 54 68 48 C63 50 60 54 60 62 Z"
                        fill="#5E8050"
                    />
                </g>
            );
        case 5:
            return (
                <g>
                    {shadow}
                    <path
                        d="M60 146 V54"
                        stroke={STEM}
                        strokeWidth="6"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <g transform="translate(60 122)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf)" />
                    </g>
                    <g transform="translate(60 110) scale(-1 1)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf2)" />
                    </g>
                    <path
                        d="M60 66 C55 64 53 58 54 52 L66 52 C67 58 65 64 60 66 Z"
                        fill="#4E7047"
                    />
                    <g transform="translate(60 48)">
                        <use
                            href="#tf-petal"
                            transform="rotate(0)"
                            fill="url(#tf-cream)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(-42)"
                            fill="url(#tf-peach)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(42)"
                            fill="url(#tf-blue)"
                        />
                        <circle r="8.5" fill="url(#tf-center)" />
                    </g>
                </g>
            );
        case 6:
            return (
                <g>
                    {shadow}
                    <path
                        d="M60 148 V50"
                        stroke={STEM}
                        strokeWidth="6.5"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <g transform="translate(60 124)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf)" />
                    </g>
                    <g transform="translate(60 112) scale(-1 1)">
                        <use href="#tf-leaf-shape" fill="url(#tf-leaf2)" />
                    </g>
                    <path
                        d="M60 62 C54 60 52 54 53 47 L67 47 C68 54 66 60 60 62 Z"
                        fill="#4E7047"
                    />
                    <g transform="translate(60 44)">
                        <use
                            href="#tf-petal"
                            transform="rotate(0)"
                            fill="url(#tf-cream)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(45)"
                            fill="url(#tf-peach)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(90)"
                            fill="url(#tf-blue)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(135)"
                            fill="url(#tf-cream)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(180)"
                            fill="url(#tf-peach)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(225)"
                            fill="url(#tf-blue)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(270)"
                            fill="url(#tf-cream)"
                        />
                        <use
                            href="#tf-petal"
                            transform="rotate(315)"
                            fill="url(#tf-peach)"
                        />
                        <circle r="11.5" fill="url(#tf-center)" />
                        <circle
                            cx="-3"
                            cy="-3.5"
                            r="3.2"
                            fill="#fff"
                            opacity="0.28"
                        />
                    </g>
                </g>
            );
        default:
            return null;
    }
}

// Empty margin above the art at each stage in the 120x158 viewBox. Cropping
// it out (plus the unused side margins) keeps the young plant legible at
// nav-icon size while later stages still fill the box.
const CROP_TOP: Record<FlowerStage, number> = {
    1: 96,
    2: 92,
    3: 58,
    4: 24,
    5: 8,
    6: 4,
};

export function TrainingFlower({
    trainingDay,
    className,
    cropped = false,
}: {
    readonly trainingDay: number;
    readonly className?: string;
    /** Crop to the drawn art for small (icon-size) renders. */
    readonly cropped?: boolean;
}) {
    const stage = flowerStageForDay(trainingDay);
    const cropTop = CROP_TOP[stage];
    return (
        <svg
            viewBox={
                cropped ? `24 ${cropTop} 72 ${158 - cropTop}` : '0 0 120 158'
            }
            // Bottom-anchor the cropped art so the plant stands on the row.
            preserveAspectRatio={cropped ? 'xMidYMax meet' : undefined}
            className={className}
            aria-hidden="true"
            focusable="false"
        >
            <defs>
                <linearGradient id="tf-leaf" x1="0" y1="1" x2="1" y2="0">
                    <stop offset="0" stopColor="#3E6440" />
                    <stop offset="1" stopColor="#8FB264" />
                </linearGradient>
                <linearGradient id="tf-leaf2" x1="0" y1="1" x2="1" y2="0">
                    <stop offset="0" stopColor="#2E4A3A" />
                    <stop offset="1" stopColor="#6E9052" />
                </linearGradient>
                <radialGradient id="tf-center" cx="42%" cy="38%" r="70%">
                    <stop offset="0" stopColor="#FCC65C" />
                    <stop offset="0.55" stopColor="#F0842E" />
                    <stop offset="1" stopColor="#D65B1D" />
                </radialGradient>
                <linearGradient id="tf-cream" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#FFF3E4" />
                    <stop offset="1" stopColor="#FBDFC4" />
                </linearGradient>
                <linearGradient id="tf-peach" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#FBE0D2" />
                    <stop offset="1" stopColor="#F3C3B4" />
                </linearGradient>
                <linearGradient id="tf-blue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#E9EAFB" />
                    <stop offset="1" stopColor="#CFCFF3" />
                </linearGradient>
                <linearGradient id="tf-bud" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0" stopColor="#6E9052" />
                    <stop offset="1" stopColor="#F0A6B0" />
                </linearGradient>
                {/* Petal anchored at (0,0), tip up — rotated copies meet at the center. */}
                <path
                    id="tf-petal"
                    d="M0 0 C -7 -8 -8 -22 0 -33 C 8 -22 7 -8 0 0 Z"
                />
                {/* Leaf base at (0,0), mirrored with scale(-1 1) for the pair. */}
                <path
                    id="tf-leaf-shape"
                    d="M0 0 C -6 -10 -18 -16 -30 -15 C -27 -4 -14 3 0 0 Z"
                />
            </defs>
            <StageArt stage={stage} />
        </svg>
    );
}
